<script setup>
import AdminLayout from "../Layouts/AdminLayout.vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Tag from "primevue/tag";
import { router } from "@inertiajs/vue3";
import { ref } from "vue";

const props = defineProps({
    notifications: {
        type: Object,
        required: true
    }
});

const activeIndex = ref(1); // Set History Log tab as active

const getTypeSeverity = (type) => {
    switch(type) {
        case 'all': return 'info';
        case 'all_active': return 'info';
        case 'many': return 'warning';
        case 'customer': return 'success';
        default: return 'secondary';
    }
};

const getTypeLabel = (type) => {
    return type; // Return the type as-is: all, many, or customer
};

const getStatusSeverity = (status) => {
    switch(status) {
        case 'processing': return 'warning';
        case 'sent': return 'success';
        case 'failed': return 'danger';
        default: return 'secondary';
    }
};

const getStatusLabel = (status) => {
    switch(status) {
        case 'processing': return 'Processing';
        case 'sent': return 'Sent';
        case 'failed': return 'Failed';
        default: return status;
    }
};

const handleTabChange = (event) => {
    // If Customers tab is clicked (index 0), redirect to main notifications page
    if (event.index === 0) {
        router.visit('/admin/notifications');
    }
};

const viewDetails = (notification) => {
    router.visit(`/admin/notifications/history/${notification.uuid}`);
};
</script>

<template>
    <AdminLayout>
        <div class="p-6">
            <div class="flex justify-between items-center mb-6">
                <h1 class="text-2xl font-semibold">Notifications Dashboard</h1>
            </div>

            <TabView :activeIndex="activeIndex" @tab-change="handleTabChange">
                <TabPanel header="Customers">
                    <!-- This tab will redirect to main page when clicked -->
                </TabPanel>

                <TabPanel header="History Log">
                    <DataTable 
                        :value="notifications.data" 
                        :paginator="true"
                        :rows="20"
                        :totalRecords="notifications.total"
                        :lazy="true"
                        responsiveLayout="scroll"
                        class="mt-4"
                    >
                        <Column field="title" header="Title" :sortable="true" style="min-width: 200px">
                            <template #body="{ data }">
                                <div class="font-medium">{{ data.title }}</div>
                                <div class="text-sm text-gray-500 mt-1">{{ data.body }}</div>
                            </template>
                        </Column>
                        
                        <Column field="type" header="Type" :sortable="true" style="width: 120px">
                            <template #body="{ data }">
                                <Tag :value="data.type" :severity="getTypeSeverity(data.type)" />
                            </template>
                        </Column>
                        
                        <Column field="status" header="Status" :sortable="true" style="width: 120px">
                            <template #body="{ data }">
                                <Tag :value="getStatusLabel(data.status)" :severity="getStatusSeverity(data.status)" />
                            </template>
                        </Column>
                        
                        <Column field="created_at" header="Sent At" :sortable="true" style="width: 180px">
                            <template #body="{ data }">
                                <div>{{ data.created_at }}</div>
                                <div class="text-sm text-gray-500">{{ data.created_at_human }}</div>
                            </template>
                        </Column>
                        
                        <Column header="Actions" style="width: 100px">
                            <template #body="{ data }">
                                <Button 
                                    icon="pi pi-eye" 
                                    severity="secondary" 
                                    text 
                                    size="small"
                                    @click="viewDetails(data)"
                                    title="View Details"
                                />
                            </template>
                        </Column>
                    </DataTable>
                </TabPanel>
            </TabView>
        </div>
    </AdminLayout>
</template>